import type { ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';
import { moneyCompact } from '../../lib/format.ts';

/**
 * One stage column. The header carries the count and the summed value so the
 * board answers "where is the money sitting" without opening anything.
 *
 * `dropActive` is the styling hook for section 3's drag-to-move: the column
 * lights up when a card is held over it.
 */
export function PipelineColumn({
  title,
  count,
  totalValue,
  dropActive = false,
  children,
}: {
  title: string;
  count: number;
  totalValue: string | null;
  dropActive?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      className={cx(
        'flex w-64 shrink-0 flex-col rounded-lg border transition',
        dropActive
          ? 'border-accent-ring bg-accent-subtle'
          : 'border-border-subtle bg-surface-inset',
      )}
    >
      <header className="flex items-center justify-between gap-2 px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          <h3 className="text-xs font-medium text-fg-muted">{title}</h3>
          <span className="numeric rounded-sm bg-surface-raised px-1 py-px text-2xs text-fg-subtle">
            {count}
          </span>
        </div>
        {totalValue !== null && (
          <span className="numeric text-2xs text-fg-subtle">{moneyCompact(totalValue)}</span>
        )}
      </header>
      {/* Each column scrolls on its own. A single column with thirteen cards
          would otherwise set the height of the whole page and push every
          other column's contents off screen. */}
      <div className="board-column-scroll flex flex-col gap-2 px-2 pb-2">{children}</div>
    </section>
  );
}
